import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

const HERO_SLIDES = [
  {
    image: "https://cdn.salla.sa/PzAO/3ad80b91-d49e-48db-8370-48cebf5f2095-1000x1000-ugFaDrOQvdgGp2JqKu0yVvBm7rrsrWlCHuHCH1mX.jpg",
    alt: "Pearl Musk by Reef",
  },
  {
    image: "https://cdn.salla.sa/PzAO/484a242d-ea98-4c16-8375-8265e0dadcc8-1000x1000-DiLogdOAl3lbU8fC5Eld6PjJSGHkgdXa24gutc65.png",
    alt: "Princess Reef Collection",
  },
  {
    image: "https://cdn.salla.sa/PzAO/5ca3f2c7-ca33-4b70-bafb-19bc8a314c92-1000x1000-17XXtHBAXakNN4VcKkOVVbU7tu0wcR3x4X7hKeNL.jpg",
    alt: "Reef 33",
  },
  {
    image: "https://cdn.salla.sa/PzAO/e69356ef-d29c-480e-b9cf-4ed9b2abd711-1000x1000-J5uPxIkefjJbvteAug6sNdHoTtroXaUkYUo1o02M.jpg",
    alt: "Arab Abaya by Reef",
  },
  {
    image: "https://cdn.salla.sa/PzAO/93892c33-50dc-4637-972c-575f7543f51f-1000x1000-Oz06xso86C94c9m75ELIMzOQFcbg7pwoMSp2C13l.jpg",
    alt: "Veridian by Reef",
  },
];

const HeroSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZooming, setIsZooming] = useState(true);

  // Preload all images
  useEffect(() => {
    HERO_SLIDES.forEach(({ image }) => {
      const img = new Image();
      img.src = image;
    });
  }, []);

  const nextSlide = useCallback(() => {
    setIsZooming(false);
    setTimeout(() => {
      setCurrentIndex(prev => (prev + 1) % HERO_SLIDES.length);
      setIsZooming(true);
    }, 50);
  }, []);

  // Auto-advance every 5.5 seconds
  useEffect(() => {
    const timer = setInterval(nextSlide, 5500);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <section 
      className="relative h-screen overflow-hidden"
      data-testid="hero-section"
    >
      {/* Slideshow Background Images */}
      {HERO_SLIDES.map(({ image, alt }, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-[1500ms] ease-in-out"
          style={{ opacity: currentIndex === i ? 1 : 0 }}
        >
          <img
            src={image}
            alt={alt}
            className="w-full h-full object-cover"
            style={{
              transform: currentIndex === i && isZooming ? 'scale(1.15)' : 'scale(1.05)',
              transition: 'transform 6s ease-out',
            }}
            fetchPriority={i === 0 ? "high" : "low"}
            data-testid={i === 0 ? "hero-image" : undefined}
            loading={i === 0 ? "eager" : "lazy"}
          />
        </div>
      ))}

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-[#050505]/50" />

      {/* Golden Radial Glow */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 40%, rgba(212, 175, 55, 0.18) 0%, transparent 55%)',
        }}
      />

      {/* Gold Particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="gold-particle"
            style={{
              left: `${38 + i * 10}%`,
              top: `${30 + (i % 2) * 15}%`,
              animationDelay: `${i * 1.2}s`,
            }}
          />
        ))}
      </div>

      {/* Arabian Pattern */}
      <div className="absolute inset-0 arabian-pattern opacity-10" />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/20 to-transparent" />

      {/* Content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center z-30 px-6">
        {/* Arabic Element */}
        <div className="mb-6 animate-fade-in">
          <span className="font-arabic text-3xl md:text-4xl text-[#D4AF37]">عطر</span>
        </div>

        {/* Brand */}
        <div className="mb-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <h1 
            className="text-4xl md:text-6xl lg:text-8xl tracking-[0.15em] text-white drop-shadow-2xl"
            data-testid="hero-title"
            style={{ fontFamily: "'Aref Ruqaa', serif", fontWeight: 700 }}
          >
            QASR <span className="text-[#D4AF37]">PARFUMERIE</span>
          </h1>
        </div>

        {/* Tagline */}
        <p
          className="font-heading text-base md:text-lg tracking-[0.4em] text-[#D4AF37] mb-4 animate-fade-in"
          style={{ animationDelay: '0.4s' }}
          data-testid="hero-tagline"
        >
          THE QUINTESSENCE OF ARABIAN ELEGANCE
        </p>

        {/* Arabic Subtitle */}
        <p
          className="font-arabic text-xl md:text-2xl text-white/70 mb-12 animate-fade-in"
          style={{ animationDelay: '0.5s' }}
        >
          جوهر الأناقة العربية
        </p>

        {/* CTA Buttons */}
        <div
          className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in"
          style={{ animationDelay: '0.6s' }}
        >
          <Link to="/products">
            <button
              data-testid="hero-explore-button"
              className="bg-[#D4AF37] text-[#050505] px-10 py-4 font-heading text-xs tracking-[0.2em] hover:bg-[#E5C158] transition-colors duration-200"
            >
              EXPLORE COLLECTION
            </button>
          </Link>
          <Link to="/about">
            <button
              data-testid="hero-story-button"
              className="bg-[#050505]/50 backdrop-blur-sm border border-[#D4AF37]/50 text-[#D4AF37] px-10 py-4 font-heading text-xs tracking-[0.2em] hover:bg-[#D4AF37]/10 transition-colors duration-200"
            >
              DISCOVER OUR STORY
            </button>
          </Link>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-30 flex gap-2">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => { setCurrentIndex(i); setIsZooming(true); }}
            data-testid={`hero-slide-indicator-${i}`}
            className={`h-[2px] transition-all duration-500 ${
              currentIndex === i 
                ? 'w-8 bg-[#D4AF37]' 
                : 'w-4 bg-white/30 hover:bg-white/50'
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30 animate-fade-in" style={{ animationDelay: '1s' }}>
        <div className="text-[#D4AF37]/70 flex flex-col items-center">
          <span className="font-heading text-xs tracking-[0.3em] mb-2">SCROLL</span>
          <ChevronDown size={20} className="bounce-arrow" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
